import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import Navigation from '../components/Navigation/Navigation';
import { useChurch } from '../contexts/ChurchContext';
import { useConfirmModal } from '../components/common/ConfirmModal';
import { liturgicalYearService, LiturgicalYear, LiturgicalCalendarService, NewServiceData } from '../services/liturgicalYearService';
import './LiturgicalYearSetupPage.css';

interface UserService {
  service_id?: number;
  calendar_id?: number;
  liturgical_year_id: number;
  liturgy_date: string;
  time?: string;
  liturgy_name: string;
  ot_reading?: string;
  nt_reading?: string;
  gospel_reading?: string;
  psalm_reading?: string;
  special_service_name?: string;
  created_at?: string;
  updated_at?: string;
  season?: string;
  is_special?: boolean;
  church_id?: number;
  service_def_id?: number;
  church_name?: string;
}

const LiturgicalYearSetupPage: React.FC = () => {
  const { confirm, ConfirmModal } = useConfirmModal();
  const { currentChurch, userChurches, isLoadingChurches } = useChurch();

  // Convert "12:30 PM" to "12:30" (24-hour format)
  const convertTo24Hour = (time12h: string): string => {
    const match = time12h.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!match) return time12h; // Return as-is if not in expected format

    let [_, hours, minutes, period] = match;
    let hour = parseInt(hours);

    if (period.toUpperCase() === 'PM' && hour !== 12) {
      hour += 12;
    } else if (period.toUpperCase() === 'AM' && hour === 12) {
      hour = 0;
    }

    return `${hour.toString().padStart(2, '0')}:${minutes}`;
  };
  const [liturgicalYears, setLiturgicalYears] = useState<LiturgicalYear[]>([]);
  const [yearsWithServices, setYearsWithServices] = useState<{ liturgical_year: string; service_count: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [editingService, setEditingService] = useState<LiturgicalCalendarService | UserService | null>(null);
  const [serviceFormData, setServiceFormData] = useState({
    liturgy_name: '',
    liturgy_date: '',
    time: '10:00',
    ot_reading: '',
    nt_reading: '',
    gospel_reading: '',
    psalm_reading: ''
  });
  const [showViewServicesModal, setShowViewServicesModal] = useState(false);
  const [viewingYear, setViewingYear] = useState<string>('');
  const [viewingServices, setViewingServices] = useState<UserService[]>([]);
  const [showCreateServicesModal, setShowCreateServicesModal] = useState(false);
  const [serviceTimes, setServiceTimes] = useState<string[]>(['10:00']);
  const [selectedLiturgicalYear, setSelectedLiturgicalYear] = useState<string>('');
  const [showOverwriteModal, setShowOverwriteModal] = useState(false);
  const [serviceDefinitions, setServiceDefinitions] = useState<any[]>([]);
  const [selectedChurches, setSelectedChurches] = useState<number[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const loadServiceDefinitions = async () => {
      if (userChurches && userChurches.length > 0) {
        try {
          // Load service definitions for all churches
          const allServiceDefs = [];
          for (const church of userChurches) {
            try {
              const serviceDefs = await liturgicalYearService.getServiceDefinitionsForChurch(church.id);
              allServiceDefs.push(...serviceDefs);
            } catch (error) {
              console.error(`Error loading service definitions for church ${church.id}:`, error);
            }
          }
          setServiceDefinitions(allServiceDefs);
        } catch (error) {
          console.error('Error loading service definitions:', error);
        }
      }
    };

    loadServiceDefinitions();
  }, [userChurches]);

  const loadData = async () => {
    try {
      const [years, servicesYears] = await Promise.all([
        liturgicalYearService.getAllLiturgicalYears(),
        liturgicalYearService.getLiturgicalYearsWithServices()
      ]);
      console.log('📋 Loaded liturgical years:', years);
      setLiturgicalYears(years);
      setYearsWithServices(servicesYears);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load liturgical years');
    } finally {
      setLoading(false);
    }
  };


  const formatLiturgicalYearName = (name: string) => {
    return name.replace(/_/g, ' - ');
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'No date';

    try {
      // Parse date components directly to avoid timezone issues
      const dateOnly = dateString.split('T')[0]; // Handle both "2026-01-06" and "2026-01-06T..."
      const [year, month, day] = dateOnly.split('-').map(Number);

      // Check if date components are valid
      if (isNaN(year) || isNaN(month) || isNaN(day)) {
        console.error('Invalid date string:', dateString);
        return 'Invalid date';
      }

      // Create date using UTC to avoid timezone shifts
      const date = new Date(Date.UTC(year, month - 1, day));

      const formatted = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC' // Use UTC to match the date exactly
      });

      console.log('[SETTINGS formatDate] Input:', dateString, '→ Parsed:', {year, month, day}, '→ UTC Date:', date.toISOString(), '→ Formatted:', formatted);

      return formatted;
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return 'Invalid date';
    }
  };

  const getServiceKey = (service: UserService | LiturgicalCalendarService): string => {
    if ('service_id' in service && service.service_id) {
      return `service-${service.service_id}`;
    }
    return `calendar-${service.calendar_id}`;
  };

  const getServiceTime = (service: UserService | LiturgicalCalendarService): string | undefined => {
    const time = 'time' in service ? service.time : undefined;
    // Convert HH:MM:SS to HH:MM for HTML time input
    if (time && time.includes(':')) {
      const parts = time.split(':');
      if (parts.length >= 2) {
        return `${parts[0]}:${parts[1]}`;
      }
    }
    return time;
  };

  const formatTime12Hour = (time24: string | undefined): string => {
    if (!time24) return '';

    const parts = time24.split(':');
    if (parts.length < 2) return time24;

    let hours = parseInt(parts[0]);
    const minutes = parts[1];
    const period = hours >= 12 ? 'PM' : 'AM';

    // Convert to 12-hour format
    if (hours === 0) {
      hours = 12;
    } else if (hours > 12) {
      hours = hours - 12;
    }

    return `${hours}:${minutes} ${period}`;
  };

  const groupServicesBySeason = (services: (UserService | LiturgicalCalendarService)[]) => {
    const grouped = services.reduce((acc, service) => {
      const season = service.season || 'Other Services';
      if (!acc[season]) {
        acc[season] = [];
      }
      acc[season].push(service);
      return acc;
    }, {} as Record<string, (UserService | LiturgicalCalendarService)[]>);

    // Sort seasons liturgically
    const seasonOrder = ['Advent', 'Christmas', 'Epiphany', 'Lent', 'Holy Week', 'Easter', 'Pentecost - Summer', 'Pentecost - Fall', 'Pentecost', 'Other Services'];
    const sortedSeasons = Object.keys(grouped).sort((a, b) => {
      const aIndex = seasonOrder.indexOf(a);
      const bIndex = seasonOrder.indexOf(b);
      if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });

    return sortedSeasons.map(season => ({
      season,
      services: grouped[season].sort((a: UserService | LiturgicalCalendarService, b: UserService | LiturgicalCalendarService) => {
        // First sort by date
        const dateCompare = new Date(a.liturgy_date).getTime() - new Date(b.liturgy_date).getTime();
        if (dateCompare !== 0) return dateCompare;

        // If dates are equal, sort by time
        const aTime = ('time' in a ? a.time : undefined) || '00:00';
        const bTime = ('time' in b ? b.time : undefined) || '00:00';
        return aTime.localeCompare(bTime);
      })
    }));
  };


  const handleServiceFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!viewingYear) {
      toast.error('Please select a liturgical year');
      return;
    }

    const selectedYear = liturgicalYears.find(year => year.liturgical_year === viewingYear);
    if (!selectedYear) {
      toast.error('Selected liturgical year not found');
      return;
    }

    try {
      const serviceData: NewServiceData = {
        liturgical_year_id: selectedYear.liturgical_year_id,
        liturgy_date: serviceFormData.liturgy_date,
        time: serviceFormData.time,
        liturgy_name: serviceFormData.liturgy_name,
        ot_reading: serviceFormData.ot_reading || undefined,
        nt_reading: serviceFormData.nt_reading || undefined,
        gospel_reading: serviceFormData.gospel_reading || undefined,
        psalm_reading: serviceFormData.psalm_reading || undefined
      };

      if (editingService) {
        serviceData.calendar_id = editingService.calendar_id;
        const serviceId = 'service_id' in editingService && editingService.service_id 
          ? editingService.service_id 
          : editingService.calendar_id;
        if (serviceId) {
          await liturgicalYearService.updateService(serviceId, serviceData);
          toast.success('Service updated successfully');
        }
      } else {
        await liturgicalYearService.createService(serviceData);
        toast.success('Service created successfully');
      }

      setShowServiceForm(false);
      // If we're viewing services, refresh that view too
      if (viewingYear && showViewServicesModal) {
        const updatedServices = await liturgicalYearService.getUserServicesForLiturgicalYear(viewingYear);
        setViewingServices(updatedServices);
      }
      loadData(); // Refresh the years with services
    } catch (error) {
      console.error('Error saving service:', error);
      toast.error('Failed to save service');
    }
  };

  const handleServiceFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setServiceFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleViewServices = async (year: string) => {
    setViewingYear(year);
    setServicesLoading(true);
    try {
      const services = await liturgicalYearService.getUserServicesForLiturgicalYear(year);
      console.log('[SETTINGS API Response] Year:', year, 'Services:', services.filter(s => s.liturgy_name?.includes('Epiphany') || s.liturgy_date?.includes('2026-01-04')));
      setViewingServices(services);
      setShowViewServicesModal(true);
    } catch (error) {
      console.error('Error loading services:', error);
      toast.error('Failed to load services');
    } finally {
      setServicesLoading(false);
    }
  };

  const handleEditViewService = (service: UserService | LiturgicalCalendarService) => {
    setEditingService(service);
    setServiceFormData({
      liturgy_name: service.liturgy_name,
      liturgy_date: service.liturgy_date.split('T')[0],
      time: getServiceTime(service) || '10:00',
      ot_reading: service.ot_reading || '',
      nt_reading: service.nt_reading || '',
      gospel_reading: service.gospel_reading || '',
      psalm_reading: service.psalm_reading || ''
    });
    setShowServiceForm(true);
  };

  const handleDeleteViewService = (service: UserService | LiturgicalCalendarService) => {
    confirm({
      title: 'Delete Service',
      message: 'Are you sure you want to delete this service? This will also remove all associated music selections.',
      confirmText: 'Delete',
      variant: 'danger',
      onConfirm: async () => {
        try {
          const serviceId = 'service_id' in service && service.service_id
            ? service.service_id
            : service.calendar_id;

          if (serviceId) {
            await liturgicalYearService.deleteService(serviceId);
            toast.success('Service deleted successfully');

            // Refresh the view
            if (viewingYear) {
              const updatedServices = await liturgicalYearService.getUserServicesForLiturgicalYear(viewingYear);
              setViewingServices(updatedServices);
            }
            loadData(); // Refresh the main data
          }
        } catch (error) {
          console.error('Error deleting service:', error);
          toast.error('Failed to delete service');
        }
      }
    });
  };

  const handleAddViewService = () => {
    setEditingService(null);
    setServiceFormData({
      liturgy_name: '',
      liturgy_date: '',
      time: '10:00',
      ot_reading: '',
      nt_reading: '',
      gospel_reading: '',
      psalm_reading: ''
    });
    setShowServiceForm(true);
  };

  const handleServiceTimeChange = (index: number, newTime: string) => {
    const updatedTimes = [...serviceTimes];
    updatedTimes[index] = newTime;
    setServiceTimes(updatedTimes);
  };

  const addServiceTime = () => {
    setServiceTimes([...serviceTimes, '10:00']);
  };

  const removeServiceTime = (index: number) => {
    if (serviceTimes.length > 1) {
      const updatedTimes = serviceTimes.filter((_, i) => i !== index);
      setServiceTimes(updatedTimes);
    }
  };

  const handleCreateServices = async () => {
    if (!selectedLiturgicalYear) {
      toast.error('Please select a liturgical year');
      return;
    }

    if (selectedChurches.length === 0) {
      toast.error('Please select at least one church');
      return;
    }

    try {
      setServicesLoading(true);
      console.log('🚀 Starting service creation for:', selectedLiturgicalYear);
      console.log('🏛️ Selected churches:', selectedChurches);
      console.log('⏰ Service times:', serviceTimes);

      // Get the liturgical year ID
      const selectedYear = liturgicalYears.find(year => year.liturgical_year === selectedLiturgicalYear);
      console.log('📅 Selected year object:', selectedYear);

      if (!selectedYear) {
        console.error('❌ Selected liturgical year not found in list');
        toast.error('Selected liturgical year not found');
        return;
      }

      // Check if this year already has services
      const existingServices = await liturgicalYearService.getUserServicesForLiturgicalYear(selectedLiturgicalYear);
      console.log('🔍 Existing services count:', existingServices.length);

      if (existingServices.length > 0) {
        // Show overwrite/add modal
        setShowOverwriteModal(true);
        return;
      }

      await createServicesForYear(selectedYear);

    } catch (error) {
      console.error('💥 Error creating services:', error);
      toast.error('Failed to create services');
    } finally {
      setServicesLoading(false);
    }
  };

  const createServicesForYear = async (selectedYear: LiturgicalYear, overwrite = false) => {
    try {
      console.log('📋 Getting liturgical calendar template services...');
      
      // Get the liturgical calendar template services
      const templateServices = await liturgicalYearService.getServicesForLiturgicalYear(selectedLiturgicalYear);
      console.log('📝 Template services received:', templateServices.length);
      console.log('🔍 First few template services:', templateServices.slice(0, 3));
      
      if (templateServices.length === 0) {
        console.warn('⚠️ No template services found for this liturgical year');
        toast.error('No template services found for this liturgical year');
        return;
      }

      if (overwrite) {
        console.log('🗑️ Overwriting existing services...');
        // Delete all existing services for this liturgical year
        const existingServices = await liturgicalYearService.getUserServicesForLiturgicalYear(selectedLiturgicalYear);
        for (const service of existingServices) {
          if (service.service_id) {
            await liturgicalYearService.deleteService(service.service_id);
          }
        }
        console.log(`🗑️ Deleted ${existingServices.length} existing services`);
      }
      
      // Create services for each template service and each selected church
      const servicesToCreate = [];

      console.log('🏛️ Selected churches:', selectedChurches);
      console.log('🔧 All service definitions:', serviceDefinitions);

      // Loop through each selected church
      for (const churchId of selectedChurches) {
        const church = userChurches.find(c => c.id === churchId);
        const churchServiceDefinitions = serviceDefinitions.filter(def => def.church_id === churchId);
        const useServiceDefinitions = churchServiceDefinitions.length > 0;

        console.log(`🏛️ Processing church: ${church?.name} (ID: ${churchId})`);
        console.log(`🔧 Service definitions for this church:`, churchServiceDefinitions);
        console.log(`⚙️ Using service definitions:`, useServiceDefinitions);

        for (const template of templateServices) {
          if (useServiceDefinitions) {
            // Use actual service definitions from the church
            for (const serviceDef of churchServiceDefinitions) {
              servicesToCreate.push({
                liturgical_year_id: selectedYear.liturgical_year_id,
                liturgy_date: template.liturgy_date,
                time: convertTo24Hour(serviceDef.time), // Convert to 24-hour format
                // Name is the liturgical day only. Which service it is
                // already lives in church_id, service_def_id and time.
                liturgy_name: template.liturgy_name,
                ot_reading: template.ot_reading || undefined,
                nt_reading: template.nt_reading || undefined,
                gospel_reading: template.gospel_reading || undefined,
                psalm_reading: template.psalm_reading || undefined,
                calendar_id: template.calendar_id,
                church_id: churchId,
                service_def_id: serviceDef.service_def_id
              });
            }
          } else {
            // Fall back to arbitrary service times (legacy behavior)
            console.log('⚠️ No service definitions found for this church, using arbitrary times');
            for (let i = 0; i < serviceTimes.length; i++) {
              const time = serviceTimes[i];
              const serviceName = serviceTimes.length === 1
                ? template.liturgy_name
                : `${template.liturgy_name} - ${time}`;

              servicesToCreate.push({
                liturgical_year_id: selectedYear.liturgical_year_id,
                liturgy_date: template.liturgy_date,
                time: time,
                liturgy_name: serviceName,
                ot_reading: template.ot_reading || undefined,
                nt_reading: template.nt_reading || undefined,
                gospel_reading: template.gospel_reading || undefined,
                psalm_reading: template.psalm_reading || undefined,
                calendar_id: template.calendar_id,
                church_id: churchId
              });
            }
          }
        }
      }

      console.log('📦 Services to create:', servicesToCreate.length);
      console.log('🔍 First service to create:', servicesToCreate[0]);

      // Create all services
      let createdCount = 0;
      for (const serviceData of servicesToCreate) {
        try {
          console.log(`➕ Creating service ${createdCount + 1}/${servicesToCreate.length}:`, serviceData.liturgy_name);
          await liturgicalYearService.createService(serviceData);
          createdCount++;
        } catch (serviceError) {
          console.error('❌ Failed to create service:', serviceData.liturgy_name, serviceError);
        }
      }

      console.log(`✅ Successfully created ${createdCount} out of ${servicesToCreate.length} services`);

      const churchNames = selectedChurches
        .map(id => userChurches.find(c => c.id === id)?.name)
        .filter(Boolean)
        .join(', ');

      toast.success(`Successfully created ${createdCount} services for ${selectedChurches.length} church${selectedChurches.length > 1 ? 'es' : ''}: ${churchNames}!`);
      setShowCreateServicesModal(false);
      setShowOverwriteModal(false);

      // Reset form
      setSelectedLiturgicalYear('');
      setSelectedChurches([]);
      setServiceTimes(['10:00']);

      // Refresh data
      loadData();
      
    } catch (error) {
      console.error('💥 Error in createServicesForYear:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="container mt-4">
          <div className="d-flex justify-content-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="container mt-4">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center">
            <Calendar className="me-2" size={32} />
            <h1>Liturgical Year Setup</h1>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateServicesModal(true)}
          >
            <Plus className="me-2" size={16} />
            Create Services for a Liturgical Year
          </button>
        </div>


        {/* Service Add/Edit Form Modal */}
        {showServiceForm && (
          <div className="modal fade show d-block modal-layer-2" style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <form onSubmit={handleServiceFormSubmit}>
                  <div className="modal-header">
                    <h5 className="modal-title">
                      {editingService ? 'Edit Service' : 'Add New Service'}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowServiceForm(false)}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">Service Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="liturgy_name"
                        value={serviceFormData.liturgy_name}
                        onChange={handleServiceFormChange}
                        required
                        placeholder="e.g., Sunday Morning Worship, Christmas Eve Service"
                      />
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Date</label>
                          <input
                            type="date"
                            className="form-control"
                            name="liturgy_date"
                            value={serviceFormData.liturgy_date}
                            onChange={handleServiceFormChange}
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Time</label>
                          <input
                            type="time"
                            className="form-control"
                            name="time"
                            value={serviceFormData.time}
                            onChange={handleServiceFormChange}
                            required
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Readings Section */}
                    <div className="mt-4">
                      <h6 className="text-secondary mb-3">Readings (Optional)</h6>
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Old Testament</label>
                            <input
                              type="text"
                              className="form-control"
                              name="ot_reading"
                              value={serviceFormData.ot_reading}
                              onChange={handleServiceFormChange}
                              placeholder="e.g., Isaiah 9:2-7"
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Psalm</label>
                            <input
                              type="text"
                              className="form-control"
                              name="psalm_reading"
                              value={serviceFormData.psalm_reading}
                              onChange={handleServiceFormChange}
                              placeholder="e.g., Psalm 96"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">New Testament</label>
                            <input
                              type="text"
                              className="form-control"
                              name="nt_reading"
                              value={serviceFormData.nt_reading}
                              onChange={handleServiceFormChange}
                              placeholder="e.g., Titus 2:11-14"
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Gospel</label>
                            <input
                              type="text"
                              className="form-control"
                              name="gospel_reading"
                              value={serviceFormData.gospel_reading}
                              onChange={handleServiceFormChange}
                              placeholder="e.g., Luke 2:1-14"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="alert alert-info">
                      <small>
                        <strong>Note:</strong> This service will be added to the <strong>{formatLiturgicalYearName(viewingYear)}</strong> liturgical year.
                        You can also edit these details later on the Planning page.
                      </small>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowServiceForm(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                    >
                      {editingService ? 'Update Service' : 'Add Service'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Current Services */}
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h3>Current Services</h3>
                <small className="text-muted">Liturgical years that have been set up for planning</small>
              </div>
              <div className="card-body">
                {yearsWithServices.length === 0 ? (
                  <p className="text-muted">
                    No liturgical years with services found. Use "Create Services for a Liturgical Year" to get started.
                  </p>
                ) : (
                  <div className="list-group">
                    {yearsWithServices.map((year, index) => (
                      <div
                        key={index}
                        className="list-group-item"
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="mb-1">{formatLiturgicalYearName(year.liturgical_year)}</h6>
                            <small className="text-muted">
                              {year.service_count} service{year.service_count !== 1 ? 's' : ''}
                            </small>
                          </div>
                          <button 
                            className="btn btn-sm btn-outline-primary"
                            title="View Services"
                            onClick={() => handleViewServices(year.liturgical_year)}
                          >
                            <Edit size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* View Services Modal */}
        {showViewServicesModal && (
          <div className="modal fade show d-block modal-layer-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title">
                    <Calendar className="me-2" size={20} />
                    Services for {formatLiturgicalYearName(viewingYear)}
                  </h5>
                  <div className="d-flex align-items-center gap-2">
                    <button 
                      className="btn btn-light btn-sm"
                      onClick={() => handleAddViewService()}
                    >
                      <Plus className="me-1" size={14} />
                      Add Service
                    </button>
                    <button
                      type="button"
                      className="btn-close btn-close-white"
                      onClick={() => setShowViewServicesModal(false)}
                    ></button>
                  </div>
                </div>
                <div className="modal-body p-0">
                  {servicesLoading ? (
                    <div className="d-flex justify-content-center p-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : viewingServices.length === 0 ? (
                    <div className="p-5 text-center">
                      <p className="text-muted">No services found for this liturgical year.</p>
                    </div>
                  ) : (
                    <div className="services-calendar-view" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                      {groupServicesBySeason(viewingServices).map(({ season, services: seasonServices }) => (
                        <div key={season} className="season-section" data-season={season}>
                          <div className="season-header sticky-top bg-light border-bottom px-4 py-3">
                            <h5 className="mb-0 text-primary fw-bold">{season}</h5>
                            <small className="text-muted">{seasonServices.length} services</small>
                          </div>
                          <div className="services-grid">
                            {seasonServices.map((service: UserService | LiturgicalCalendarService) => (
                              <div key={getServiceKey(service)} className="service-card border-0 shadow-sm m-3 p-4 rounded">
                                <div className="d-flex justify-content-between align-items-start mb-3">
                                  <div className="flex-grow-1">
                                    <h6 className="service-name mb-2 fw-bold">{service.liturgy_name}</h6>
                                    {'church_name' in service && service.church_name && (
                                      <div className="text-muted small mb-1">
                                        <strong>{service.church_name}</strong>
                                      </div>
                                    )}
                                    <div className="service-date text-muted">
                                      <Calendar size={16} className="me-2" />
                                      {formatDate(service.liturgy_date)}
                                      {getServiceTime(service) && (
                                        <span className="ms-2">at {formatTime12Hour(getServiceTime(service))}</span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="d-flex align-items-center gap-2">
                                    {service.is_special && (
                                      <span className="badge bg-warning text-dark">Special Service</span>
                                    )}
                                    <div className="btn-group btn-group-sm">
                                      <button 
                                        className="btn btn-outline-primary" 
                                        title="Edit Service"
                                        onClick={() => handleEditViewService(service)}
                                      >
                                        <Edit size={12} />
                                      </button>
                                      <button 
                                        className="btn btn-outline-danger" 
                                        title="Delete Service"
                                        onClick={() => handleDeleteViewService(service)}
                                      >
                                        <Trash2 size={12} />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="modal-footer bg-light">
                  <div className="me-auto text-muted">
                    Total: {viewingServices.length} services
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowViewServicesModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Services Modal */}
        {showCreateServicesModal && (
          <div className="modal fade show d-block modal-layer-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <Calendar className="me-2" size={20} />
                    Create Services for a Liturgical Year
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowCreateServicesModal(false);
                      setSelectedChurches([]);
                    }}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-4">
                    <label className="form-label">Select Liturgical Year</label>
                    <select
                      className="form-select"
                      value={selectedLiturgicalYear}
                      onChange={(e) => {
                        console.log('🎯 Selected liturgical year from dropdown:', e.target.value);
                        setSelectedLiturgicalYear(e.target.value);
                      }}
                      required
                    >
                      <option value="">Choose a liturgical year...</option>
                      {liturgicalYears.map((year) => (
                        <option key={year.liturgical_year_id} value={year.liturgical_year}>
                          {formatLiturgicalYearName(year.liturgical_year)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Church Selection */}
                  <div className="mb-4">
                    <label className="form-label">Select Churches</label>
                    <div className="border rounded p-3 bg-light">
                      {userChurches.map((church) => (
                        <div key={church.id} className="form-check mb-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`church-${church.id}`}
                            checked={selectedChurches.includes(church.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedChurches([...selectedChurches, church.id]);
                              } else {
                                setSelectedChurches(selectedChurches.filter(id => id !== church.id));
                              }
                            }}
                          />
                          <label className="form-check-label" htmlFor={`church-${church.id}`}>
                            <strong>{church.name}</strong>
                            {church.address && <span className="text-muted ms-2">({church.address})</span>}
                          </label>
                        </div>
                      ))}
                      {userChurches.length === 0 && (
                        <p className="text-muted mb-0">No churches available</p>
                      )}
                    </div>
                    <div className="form-text">
                      Services will be created for each selected church using their service definitions.
                    </div>
                  </div>

                  {/* Service Creation Mode Info */}
                  <div className="mb-4">
                    {(() => {
                      if (selectedChurches.length === 0) {
                        return (
                          <div className="alert alert-warning">
                            <strong>⚠️ No churches selected</strong>
                            <p className="mb-0 mt-1">Please select at least one church to create services.</p>
                          </div>
                        );
                      }

                      const selectedChurchServiceDefs = serviceDefinitions.filter(def =>
                        selectedChurches.includes(def.church_id)
                      );

                      if (selectedChurchServiceDefs.length > 0) {
                        // Group by church
                        const defsByChurch = selectedChurches.map(churchId => {
                          const church = userChurches.find(c => c.id === churchId);
                          const defs = selectedChurchServiceDefs.filter(def => def.church_id === churchId);
                          return { church, defs };
                        }).filter(item => item.defs.length > 0);

                        return (
                          <div className="alert alert-success">
                            <h6 className="alert-heading">✅ Using Church Service Definitions</h6>
                            <p className="mb-2">
                              Services will be created using these service definitions:
                            </p>
                            {defsByChurch.map(({ church, defs }) => (
                              <div key={church?.id} className="mb-2">
                                <strong>{church?.name}:</strong>
                                <ul className="mb-0">
                                  {defs.map(def => (
                                    <li key={def.service_def_id}>
                                      {def.service_name} at {def.time}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                            <hr />
                            <small className="text-muted">
                              This will create {selectedChurchServiceDefs.length} service(s) for each liturgical date.
                            </small>
                          </div>
                        );
                      } else {
                        const selectedChurchNames = selectedChurches
                          .map(id => userChurches.find(c => c.id === id)?.name)
                          .filter(Boolean)
                          .join(', ');

                        return (
                          <>
                            <div className="alert alert-warning">
                              <h6 className="alert-heading">⚠️ Using Manual Service Times</h6>
                              <p className="mb-0">
                                No service definitions found for <strong>{selectedChurchNames || 'selected churches'}</strong>.
                                You can configure service definitions for better organization.
                              </p>
                            </div>
                            
                            <div className="mb-3">
                              <label className="form-label">Sunday Service Times</label>
                              <div className="border rounded p-3">
                                {serviceTimes.map((time, index) => (
                                  <div key={index} className="d-flex align-items-center mb-2">
                                    <input
                                      type="time"
                                      className="form-control me-2"
                                      value={time}
                                      onChange={(e) => handleServiceTimeChange(index, e.target.value)}
                                    />
                                    {serviceTimes.length > 1 && (
                                      <button
                                        type="button"
                                        className="btn btn-outline-danger btn-sm"
                                        onClick={() => removeServiceTime(index)}
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    )}
                                  </div>
                                ))}
                                <button
                                  type="button"
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={addServiceTime}
                                >
                                  <Plus size={14} className="me-1" />
                                  Add Another Service Time
                                </button>
                              </div>
                              <div className="form-text">
                                Each Sunday service will be created at these times. Special services (Christmas Eve, Easter Vigil, etc.) will also be duplicated.
                              </div>
                            </div>
                          </>
                        );
                      }
                    })()}
                  </div>

                  <div className="alert alert-info">
                    <strong>What will be created:</strong>
                    {(() => {
                      if (selectedChurches.length === 0) {
                        return <p className="mb-0 mt-2">Please select at least one church.</p>;
                      }

                      const selectedChurchServiceDefs = serviceDefinitions.filter(def =>
                        selectedChurches.includes(def.church_id)
                      );
                      const useServiceDefinitions = selectedChurchServiceDefs.length > 0;
                      const servicesPerDay = useServiceDefinitions ? selectedChurchServiceDefs.length : (serviceTimes.length * selectedChurches.length);

                      return (
                        <ul className="mb-0 mt-2">
                          <li>All Sunday services for the liturgical year ({servicesPerDay} per Sunday)</li>
                          <li>All feast days and special services ({servicesPerDay} per day)</li>
                          <li>Approximately {servicesPerDay * 60} total services for {selectedChurches.length} church{selectedChurches.length > 1 ? 'es' : ''}</li>
                        </ul>
                      );
                    })()}
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowCreateServicesModal(false);
                      setSelectedChurches([]);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleCreateServices}
                    disabled={!selectedLiturgicalYear}
                  >
                    Create Services
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Overwrite Services Confirmation Modal */}
        {showOverwriteModal && (
          <div className="modal fade show d-block modal-layer-2" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header bg-warning text-dark">
                  <h5 className="modal-title">
                    ⚠️ Services Already Exist
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowOverwriteModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <p className="lead">
                    The liturgical year <strong>{formatLiturgicalYearName(selectedLiturgicalYear)}</strong> already has services configured.
                  </p>
                  
                  <div className="alert alert-danger">
                    <h6 className="alert-heading">⚠️ DANGER: Overwrite Option</h6>
                    <p className="mb-0">
                      <strong>Overwriting will permanently delete:</strong>
                    </p>
                    <ul className="mb-0 mt-2">
                      <li>All existing services for this liturgical year</li>
                      <li>All music selections associated with those services</li>
                      <li>All planning work done for this year</li>
                    </ul>
                    <hr />
                    <p className="mb-0 text-danger">
                      <strong>This action cannot be undone!</strong>
                    </p>
                  </div>

                  <div className="alert alert-info">
                    <h6 className="alert-heading">ℹ️ Safe Option: Add Services</h6>
                    <p className="mb-0">
                      Adding services will keep all existing services and music selections, 
                      and create additional services alongside them.
                    </p>
                  </div>

                  <p>What would you like to do?</p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowOverwriteModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={() => {
                      const selectedYear = liturgicalYears.find(year => year.liturgical_year === selectedLiturgicalYear);
                      if (selectedYear) {
                        createServicesForYear(selectedYear, false);
                      }
                    }}
                  >
                    Add Services (Safe)
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => {
                      if (window.confirm('⚠️ FINAL WARNING: This will permanently delete ALL existing services and music selections for this liturgical year. Type "DELETE" to confirm.') && 
                          window.prompt('Type "DELETE" to confirm deletion:') === 'DELETE') {
                        const selectedYear = liturgicalYears.find(year => year.liturgical_year === selectedLiturgicalYear);
                        if (selectedYear) {
                          createServicesForYear(selectedYear, true);
                        }
                      }
                    }}
                  >
                    ⚠️ Overwrite (DANGER)
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        <ConfirmModal />
      </div>
    </>
  );
};

export default LiturgicalYearSetupPage;